import React, { FC, useEffect, useState } from 'react';
import { Query } from '../_common';
import './JobManagement.css';
import { GET_ALL_JOBS_WITH_FILTERS_AND_SEARCH } from '../../graphql/client/queries';
import {
    GetAllJobsWithFiltersAndSearch_getAllJobsWithFiltersAndSearch as Job,
    GetAllJobsWithFiltersAndSearch,
    SortOrder,
} from '../../types/schemaTypes';
import { Error } from '../_common';
import JobManagementHeader from '../Header/JobManagementHeader';
import { JobFilters } from '../Filter';
import { Routes } from '../../utils';
import JobList from '../TableList/JobList';
import { useStoreContext } from '../App/StoreProvider';
import * as JobListActions from '../../store/actions/jobList';
import { useParams } from 'react-router-dom';

interface SelectedJob {
    jobId: string;
    jobName: string;
}

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;

const JobManagement: FC = (props: any): React.ReactElement => {
   // const params =useParams();
    
    const [sortOrder, setSortOrder] = useState<SortOrder | null>(null);
    const initialArray: SelectedJob[] = [];
    // TODO: Move local state to jobListReducer
    const [selectedJobs, setSelectedJobs] = useState(initialArray);
    const [listHeight, setListHeight] = useState(window.screen.height);
    const [state, dispatch] = useStoreContext();

    const [showFilter, setShowFilter] = useState(false);

    const abortController = React.useRef(new AbortController());
    const {
        jobList: { filters, searchPhrase },
    } = state;
    
    useEffect(() => {
       // debugger;
       
        dispatch(JobListActions.resetFilterState());
    }, []);

    const variables = {
        input: {
            regionIds: filters.regionIds,
            districtIds: filters.districtIds,
            statuses: filters.statuses,
            customerIds: filters.customerIds,
            searchString: searchPhrase,
            sortOrder: sortOrder,
        },
    };

    const openSelectedJobs = (): void => {
        const URL = `${window.location.origin}${Routes.JOB_PROFILE.route}`;
        selectedJobs.forEach((el): void => {
            const newTab = window.open(URL + el.jobId, '_blank');
            setTimeout(function(): void {
                if (newTab) {
                    newTab.document.title = el.jobName;
                }
            }, 100);
        });
        const result: SelectedJob[] = [];
        setSelectedJobs(result);
    };

    const toggleSortOrder = (): void => {
        if (sortOrder === null) {
            setSortOrder(SortOrder.ASCENDING);
        } else if (sortOrder === SortOrder.ASCENDING) {
            setSortOrder(SortOrder.DESCENDING);
        } else {
            setSortOrder(null);
        }
    };

    const onCheckAll = (checkedStatus: boolean, jobsList: Job[]): void => {
        const result: SelectedJob[] = [];
        if (checkedStatus === true) {
            jobsList.forEach((el: Job): void => {
                result.push({ jobId: el.jobId, jobName: el.jobName });
            });
        }
        setSelectedJobs(result);
    };

    const context = {
        fetchOptions: {
            signal: abortController.current.signal,
        },
    };

    const abortRequest = (): void => {
        
        abortController.current.abort();
    };

    const setListContainerHeight = (): void => {
        const filterDiv = document.getElementById('jobsFilterContainer');
        const listDiv = document.getElementById('jobsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };
    document.title = 'Job Management';
    return (
        <Query<GetAllJobsWithFiltersAndSearch>
            query={GET_ALL_JOBS_WITH_FILTERS_AND_SEARCH}
            variables={variables}
            fetchPolicy="network-only"
            // nextFetchPolicy ="cache-first"
            context={context}
        >
            {({
                error,
                data,
                loading,
                fetchMore,
            }): React.ReactElement | null => {
                if (error) {
                    return <Error error={error} />;
                }

                const jobsList: Job[] = [];

                if (data?.getAllJobsWithFiltersAndSearch && data.getAllJobsWithFiltersAndSearch.length !== 0) {
                    (data.getAllJobsWithFiltersAndSearch as (Job | null)[]).forEach((element: Job | null): void => {
                        if (element != null) {
                            jobsList.push(element);
                        }
                    });
                }

                const offset =
                    data && data.getAllJobsWithFiltersAndSearch
                        ? data.getAllJobsWithFiltersAndSearch.length / PAGE_SIZE
                        : 0;

                let isEndOfResults = false;
                if (
                    offset % 1 !== 0 ||
                    (!isEndOfResults &&
                        data &&
                        data.getAllJobsWithFiltersAndSearch &&
                        data.getAllJobsWithFiltersAndSearch.length < PAGE_SIZE)
                ) {
                    // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
                    isEndOfResults = true;
                }

                return (
                    <div className="JobManagement-Container">
                        <JobManagementHeader
                            onCheckAll={checked => onCheckAll(checked, jobsList)}
                            sortOrder={sortOrder}
                            toggleSortOrder={toggleSortOrder}
                            selectedJobs={selectedJobs}
                            openSelectedJobs={openSelectedJobs}
                            showFilter={showFilter}
                            setShowFilter={setShowFilter}
                        />
                        <div className="JobManagement-Body">
                            <div className="JobLoads-Container">
                                <div id="jobsFilterContainer" className="JobLoads-BodyContainer">
                                    <JobFilters
                                        abortRequest={abortRequest}
                                        onFilterToggle={setListContainerHeight}
                                        showFilter={showFilter}
                                        setShowFilter={setShowFilter}
                                    />
                                </div>
                            </div>

                            <div id="jobsListContainer" className="JobManagement-List" style={{ height: listHeight }}>
                                <JobList
                                    abortRequest={abortRequest}
                                    jobsList={jobsList}
                                    isLoading={loading}
                                    isEndOfResults={isEndOfResults}
                                    entries={((data?.getAllJobsWithFiltersAndSearch) ?? []) as (Job | null)[]}
                                    selectedJobs={selectedJobs}
                                    setSelectedJobs={setSelectedJobs}
                                    onLoadMore={() =>
                                        fetchMore({
                                            variables: {
                                                offset,
                                            },
                                            updateQuery: (prev, { fetchMoreResult }) => {
                                                if (
                                                    prev &&
                                                    fetchMoreResult &&
                                                    prev.getAllJobsWithFiltersAndSearch &&
                                                    fetchMoreResult.getAllJobsWithFiltersAndSearch
                                                ) {
                                                    return Object.assign({}, prev, {
                                                        getAllJobsWithFiltersAndSearch: [
                                                            ...prev.getAllJobsWithFiltersAndSearch,
                                                            ...fetchMoreResult.getAllJobsWithFiltersAndSearch,
                                                        ],
                                                    });
                                                } else {
                                                    return prev;
                                                }
                                            },
                                        })
                                    }
                                ></JobList>
                            </div>
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};

export default JobManagement;

